/**
 * 旅のルート再生地図
 *
 * GPS 軌跡を時系列で少しずつ描画し、旅の移動をアニメーション再生する。
 * 訪問スポットはマーカーで表示し、再生位置に応じて出現する。
 */

import { useMap } from '@vis.gl/react-google-maps'
import { Crosshair, Maximize2, Pause, Play, RotateCcw } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { TripRoutePoint, TripVisit } from '@/api/types/trip'
import { GoogleCircleMarker } from '@/components/shared/map/google-circle-marker'
import { GoogleMapCanvas } from '@/components/shared/map/google-map-canvas'
import { GooglePolyline } from '@/components/shared/map/google-polyline'
import { useFitBounds } from '@/components/shared/map/use-fit-bounds'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Slider } from '@/components/ui/slider'
import { formatTimeJST } from '@/lib/activity-format-utils'
import { cn } from '@/lib/utils'

import { tripPlaceName } from './trip-utils'

/** 1 秒あたりに進むポイント数（軌跡が長いほど速く進める） */
const BASE_POINTS_PER_SECOND = 60
const FRAME_INTERVAL_MS = 1000 / 30

/** 等倍速で全体を走破する目安の秒数 */
const BASE_DURATION_SEC = 20
const SPEEDS = [0.25, 0.5, 1, 2, 4]
const DEFAULT_SPEED_INDEX = 2

type Props = {
  points: TripRoutePoint[]
  visits: TripVisit[]
  className?: string
}

/** 再生位置に追随して描画するポリラインとマーカー */
const RouteLayer = ({
  points,
  visits,
  progressIndex,
  fitNonce,
  isFollowing,
}: {
  points: TripRoutePoint[]
  visits: TripVisit[]
  progressIndex: number
  fitNonce: number
  isFollowing: boolean
}) => {
  const map = useMap()
  const allPath = useMemo(() => points.map((p) => ({ lat: p.latitude, lng: p.longitude })), [points])
  const drawnPath = useMemo(() => allPath.slice(0, progressIndex + 1), [allPath, progressIndex])

  const currentTime = points.at(progressIndex)?.timestamp
  const visibleVisits = useMemo(() => {
    if (!currentTime) return visits
    const limit = new Date(currentTime).getTime()
    return visits.filter((v) => new Date(v.timestamp).getTime() <= limit)
  }, [visits, currentTime])

  const head = drawnPath.at(-1)

  // 件数だけだと別の旅で衝突するので始点・終点も混ぜる
  const fitKey = `${points.length}:${points.at(0)?.timestamp ?? ''}:${points.at(-1)?.timestamp ?? ''}:${fitNonce}`

  useFitBounds({ points: allPath, padding: 32, maxZoom: 14, fitKey })

  useEffect(() => {
    if (!isFollowing || !map || !head) return
    // setCenter / panTo はベクターマップのカメラアニメーションに乗って追従が遅れる
    map.moveCamera({ center: head })
  }, [map, head, isFollowing])

  return (
    <>
      {/* 全体の軌跡を薄く敷き、再生済み部分を上から濃く描く */}
      <GooglePolyline path={allPath} strokeColor="#94a3b8" strokeWeight={3} strokeOpacity={0.35} zIndex={1} />
      <GooglePolyline path={drawnPath} strokeColor="#f97316" strokeWeight={4} strokeOpacity={0.95} zIndex={2} />

      {visibleVisits.map((visit) => (
        <GoogleCircleMarker
          key={visit.id}
          position={{ lat: visit.latitude, lng: visit.longitude }}
          radius={5}
          color="#ffffff"
          fillColor="#0ea5e9"
          fillOpacity={1}
          weight={2}
          zIndex={10}
        />
      ))}

      {head && (
        <GoogleCircleMarker
          position={head}
          radius={8}
          color="#ffffff"
          fillColor="#ef4444"
          fillOpacity={1}
          weight={3}
          zIndex={20}
        />
      )}
    </>
  )
}

export const TripRouteMap = ({ points, visits, className }: Props) => {
  // 低速再生では 1 フレームあたり 1 点未満しか進まないので小数で持つ
  const [progress, setProgress] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [fitNonce, setFitNonce] = useState(0)
  const [isFollowing, setIsFollowing] = useState(false)
  const [speedIndex, setSpeedIndex] = useState(DEFAULT_SPEED_INDEX)

  const speed = SPEEDS.at(speedIndex) ?? 1
  const lastIndex = Math.max(points.length - 1, 0)
  const progressIndex = Math.round(progress)

  const stepPerFrame = useMemo(() => {
    const perSecond = Math.max(BASE_POINTS_PER_SECOND, points.length / BASE_DURATION_SEC) * speed
    return Math.max(0.05, (perSecond * FRAME_INTERVAL_MS) / 1000)
  }, [points.length, speed])

  useEffect(() => {
    setProgress(0)
    setIsPlaying(false)
  }, [points])

  useEffect(() => {
    if (!isPlaying) return

    const timer = window.setInterval(() => {
      setProgress((prev) => {
        const next = prev + stepPerFrame
        if (next >= lastIndex) {
          setIsPlaying(false)
          return lastIndex
        }
        return next
      })
    }, FRAME_INTERVAL_MS)

    return () => window.clearInterval(timer)
  }, [isPlaying, stepPerFrame, lastIndex])

  const handleToggle = useCallback(() => {
    // 最後まで再生済みなら頭出ししてから再生する
    setProgress((prev) => (prev >= lastIndex ? 0 : prev))
    setIsPlaying((prev) => !prev)
  }, [lastIndex])

  const currentPoint = points.at(progressIndex)
  const currentVisitName = useMemo(() => {
    if (!currentPoint) return null
    const limit = new Date(currentPoint.timestamp).getTime()
    const passed = visits.filter((v) => new Date(v.timestamp).getTime() <= limit)
    const last = passed.at(-1)
    return last ? tripPlaceName(last, '') : null
  }, [currentPoint, visits])

  if (points.length === 0) {
    return (
      <div className={cn('flex items-center justify-center rounded-xl border bg-muted p-8', className)}>
        <p className="text-sm text-muted-foreground">位置情報が記録されていません</p>
      </div>
    )
  }

  return (
    <div className={cn('overflow-hidden rounded-xl border', className)}>
      <GoogleMapCanvas className="h-64 w-full" mapTypeControl>
        <RouteLayer
          points={points}
          visits={visits}
          progressIndex={progressIndex}
          fitNonce={fitNonce}
          isFollowing={isFollowing}
        />
      </GoogleMapCanvas>

      {/* 幅が狭いとスライダーが短くなるので、コンテナ幅で 2 段に折り返す */}
      <div className="@container border-t bg-card">
        <div className="flex flex-wrap items-center gap-x-1 gap-y-1 px-3 py-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleToggle}
            className="shrink-0 p-2!"
            aria-label={isPlaying ? '一時停止' : '再生'}
          >
            {isPlaying ? <Pause className="size-5" /> : <Play className="size-5" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setIsPlaying(false)
              setProgress(0)
            }}
            className="shrink-0 p-2!"
            aria-label="最初に戻る"
          >
            <RotateCcw className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setIsFollowing(false)
              setFitNonce((prev) => prev + 1)
            }}
            className="shrink-0 p-2!"
            aria-label="ルート全体を表示"
          >
            <Maximize2 className="size-4" />
          </Button>
          <Button
            variant={isFollowing ? 'secondary' : 'ghost'}
            size="icon"
            onClick={() => setIsFollowing((prev) => !prev)}
            className="shrink-0 p-2!"
            aria-label="現在地点を追従"
            aria-pressed={isFollowing}
          >
            <Crosshair className={cn('size-4', isFollowing && 'text-primary')} />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="shrink-0 px-2! text-xs tabular-nums" aria-label="再生速度">
                {speed}x
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-24">
              <DropdownMenuRadioGroup
                value={String(speedIndex)}
                onValueChange={(value) => setSpeedIndex(Number(value))}
              >
                {SPEEDS.map((value, index) => (
                  <DropdownMenuRadioItem key={value} value={String(index)} className="text-xs tabular-nums">
                    {value}x
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="w-full min-w-0 @xl:ml-2 @xl:w-auto @xl:flex-1">
            <Slider
              value={[progressIndex]}
              min={0}
              max={lastIndex}
              step={1}
              onValueChange={([value]) => {
                setIsPlaying(false)
                setProgress(value ?? 0)
              }}
            />
            <p className="mt-1 truncate text-[11px] text-muted-foreground">
              {currentPoint ? formatTimeJST(currentPoint.timestamp) : '--:--'}
              {currentVisitName && ` ・ ${currentVisitName}`}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
